import { useState, useEffect, useCallback } from 'react';

import { useGoogleMaps } from './use-google-maps.hook';

import { AutocompletePrediction } from '@/types/google-maps.types';
import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { useLoading } from '@/hooks/use-loading.hook';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { getLogger } from '@/logger/logger';

const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 3;

const logger = getLogger('location.search');

export function useLocationSearch() {
  const { getGoogleMapsPredictions, isLoaded, loadError } = useGoogleMaps();
  const { isLoading, startLoading, stopLoading } = useLoading();

  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchResults, setSearchResults] = useState<AutocompletePrediction[]>([]);
  const [shouldShowEmptyResultsMessage, setShouldShowEmptyResultsMessage] = useState(false);
  const [hasSearchError, setHasSearchError] = useState(false);

  const searchValue = useDebounceValue(searchDisplayValue, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    let isCurrentSearch = true;

    async function handlePredictions() {
      startLoading();
      setHasSearchError(false);

      getAnalyticsPlugin().track(AnalyticsEnum.SEARCH_FOR_LOCATION, {
        termo: searchValue,
      });

      try {
        const predictions = await getGoogleMapsPredictions(searchValue);

        if (!isCurrentSearch) {
          return;
        }

        setShouldShowEmptyResultsMessage(!predictions.length);
        setSearchResults(predictions);
      } catch (error) {
        if (!isCurrentSearch) {
          return;
        }

        logger.error(
          'Failed to fetch location predictions',
          {
            event: 'location.search.predictions_failed',
            location: { stage: 'predictions', target: 'places' },
            error,
          },
        );
        setHasSearchError(true);
        setShouldShowEmptyResultsMessage(false);
        setSearchResults([]);
      } finally {
        if (isCurrentSearch) {
          stopLoading();
        }
      }
    }

    if (isLoaded && searchValue.length >= MIN_SEARCH_LENGTH) {
      handlePredictions();
    } else {
      stopLoading();
      setHasSearchError(false);
      setShouldShowEmptyResultsMessage(false);
      setSearchResults([]);
    }

    return () => {
      isCurrentSearch = false;
    };
  }, [searchValue, isLoaded, getGoogleMapsPredictions, startLoading, stopLoading]);

  const handleSearchInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchDisplayValue(event.target.value);
  };

  const clearSearch = useCallback(() => {
    setSearchDisplayValue('');
    setSearchResults([]);
    setShouldShowEmptyResultsMessage(false);
    setHasSearchError(false);
  }, []);

  const resetSearchResults = useCallback(() => {
    setSearchResults([]);
  }, []);

  return {
    searchDisplayValue,
    searchResults,
    isLoading,
    shouldShowEmptyResultsMessage,
    hasSearchError: hasSearchError || Boolean(loadError),
    handleSearchInput,
    clearSearch,
    resetSearchResults,
  };
}
